import { ArrowDown, ArrowUpRight, CircleCheck, Eye, Layers3, Route } from "lucide-react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { FinalCta } from "@/components/landing/FinalCta";
import { Process } from "@/components/landing/Process";
import { Reveal } from "@/components/landing/Reveal";
import { usePageMeta } from "@/hooks/use-page-meta";

const PRINCIPLES = [
  {
    icon: Eye,
    title: "Entender antes de propor",
    text: "Começamos pelo contexto, pelas pessoas e pela rotina. A tecnologia vem depois do problema estar claro.",
    signal: "Contexto",
  },
  {
    icon: Layers3,
    title: "Projetar para evoluir",
    text: "Criamos estruturas que resolvem a etapa atual sem fechar as portas para o próximo movimento do negócio.",
    signal: "Estrutura",
  },
  {
    icon: CircleCheck,
    title: "Entregar o que faz sentido",
    text: "Cada decisão precisa melhorar a experiência, simplificar a operação ou gerar clareza. Se não fizer, não entra.",
    signal: "Resultado",
  },
];

const OperationMap = () => (
  <div className="relative min-h-[440px] overflow-hidden border border-black/15 bg-[#e7e8df] p-5 sm:min-h-[520px] sm:p-8" aria-hidden="true">
    <div className="solution-paper-grid absolute inset-0 opacity-80" />
    <div className="relative flex items-center justify-between border-b border-black/15 pb-4">
      <p className="text-[9px] font-black uppercase tracking-[.18em]">Leitura da operação</p>
      <span className="h-2 w-2 bg-[#2851aa]" />
    </div>
    <svg className="absolute inset-0 h-full w-full" viewBox="0 0 700 520" preserveAspectRatio="none">
      <path d="M90 125 C240 125 230 255 350 255 S485 390 610 390" fill="none" stroke="rgba(40,81,170,.42)" strokeWidth="2" strokeDasharray="8 10" />
      <path d="M90 390 C230 390 240 255 350 255 S500 125 610 125" fill="none" stroke="rgba(5,6,6,.18)" strokeWidth="1.5" />
    </svg>
    {[
      { label: "Pessoas", x: "10%", y: "18%" },
      { label: "Rotinas", x: "72%", y: "18%" },
      { label: "Decisões", x: "10%", y: "69%" },
      { label: "Dados", x: "74%", y: "69%" },
    ].map((node) => <div key={node.label} className="absolute grid h-20 w-24 place-items-center border border-black/18 bg-[#f1f3ec] text-center text-[9px] font-bold uppercase tracking-[.13em] shadow-[7px_7px_0_rgba(5,6,6,.08)] sm:h-24 sm:w-32" style={{ left: node.x, top: node.y }}>{node.label}</div>)}
    <div className="absolute left-1/2 top-1/2 grid h-28 w-28 -translate-x-1/2 -translate-y-1/2 place-items-center bg-[#050606] text-center text-white shadow-[0_20px_60px_rgba(5,6,6,.22)] sm:h-36 sm:w-36">
      <div><Route className="mx-auto text-lime-300" size={24} /><p className="mt-3 text-[9px] font-bold uppercase tracking-[.14em]">Direção<br />do produto</p></div>
    </div>
  </div>
);

export const AboutPage = () => {
  usePageMeta("Sobre", "Conheça o Clube Digital e a forma como transformamos necessidades de negócio em soluções digitais claras e funcionais.");
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const phraseX = useTransform(scrollYProgress, [0, 1], [reduce ? "0%" : "3%", reduce ? "0%" : "-2%"]);

  return (
    <main>
      <section data-header-theme="dark" className="editorial-grid relative flex min-h-[100svh] items-end overflow-hidden bg-[#050606] pb-14 pt-36 text-white md:pb-20 md:pt-44">
        <div className="site-shell relative">
          <div className="flex items-center justify-between border-b border-white/12 pb-6">
            <p className="eyebrow">Sobre o Clube Digital</p>
            <p className="hidden text-[10px] font-bold uppercase tracking-[.18em] text-white/32 sm:block">Clareza antes da complexidade</p>
          </div>
          <h1 className="display-title mt-12 max-w-[1300px] text-[clamp(3.7rem,9vw,9.2rem)] leading-[.88]">Próximos do negócio.<br /><span className="text-white/34">Profundos na tecnologia.</span></h1>
          <div className="mt-14 flex flex-col gap-8 border-t border-white/12 pt-7 md:flex-row md:items-end md:justify-between">
            <p className="max-w-2xl text-base leading-relaxed text-white/58 md:text-lg">Somos uma empresa de desenvolvimento digital que combina estratégia, experiência e engenharia para criar soluções alinhadas à realidade de cada operação.</p>
            <a href="#como-pensamos" className="group inline-flex w-fit items-center gap-3 text-xs font-bold uppercase tracking-[.16em] text-lime-300">Como pensamos <ArrowDown size={16} className="transition-transform group-hover:translate-y-1" /></a>
          </div>
        </div>
      </section>

      <section ref={ref} id="como-pensamos" data-header-theme="light" className="overflow-hidden bg-[#f1f3ec] py-24 text-[#050606] md:py-36">
        <div className="site-shell">
          <div className="flex items-center justify-between gap-8 border-b border-black/15 pb-6">
            <p className="eyebrow !text-black/55">Nosso ponto de entrada</p>
            <p className="hidden text-[10px] font-bold uppercase tracking-[.18em] text-black/30 sm:block">Antes da solução</p>
          </div>
          <motion.h2 style={{ x: phraseX }} className="display-title mt-16 max-w-[1320px] text-[clamp(3.5rem,8.4vw,8.7rem)] leading-[.9]">A gente entra antes do código.</motion.h2>
          <div className="mt-16 grid gap-12 border-t border-black/15 pt-10 lg:grid-cols-[.72fr_1.28fr] lg:gap-20">
            <div className="flex flex-col justify-between">
              <p className="display-title max-w-lg text-3xl leading-[1.06] md:text-5xl">Entender o todo é o que permite construir somente o necessário.</p>
              <div className="mt-10 max-w-md space-y-5 text-sm leading-relaxed text-black/55 md:text-base">
                <p>Não chegamos com uma resposta pronta. Entramos na operação para enxergar onde informação, rotina e decisão deixaram de conversar.</p>
                <p>É desse entendimento que nasce um produto simples por fora, consistente por dentro e útil desde o primeiro uso.</p>
              </div>
            </div>
            <OperationMap />
          </div>
        </div>
      </section>

      <section data-header-theme="dark" className="bg-[#050606] py-24 text-white md:py-36">
        <div className="site-shell">
          <Reveal className="grid gap-10 border-b border-white/12 pb-12 lg:grid-cols-[.7fr_1.3fr] lg:items-end">
            <p className="eyebrow">O que orienta cada decisão</p>
            <h2 className="display-title text-[clamp(3rem,6.6vw,7rem)] leading-[.92]">Critério antes de volume.</h2>
          </Reveal>
          <div>
            {PRINCIPLES.map(({ icon: Icon, title, text, signal }, index) => (
              <Reveal key={title} delay={index * .06}>
                <article className="group grid gap-7 border-b border-white/12 py-10 sm:grid-cols-[64px_.9fr_1.1fr_auto] sm:items-center md:py-12">
                  <span className="grid h-12 w-12 place-items-center border border-white/15 text-cyan-300 transition-colors group-hover:border-lime-300 group-hover:text-lime-300"><Icon size={20} /></span>
                  <h3 className="display-title text-2xl sm:text-3xl">{title}</h3>
                  <p className="max-w-xl text-sm leading-relaxed text-white/48 md:text-base">{text}</p>
                  <p className="text-[9px] font-bold uppercase tracking-[.18em] text-white/25 group-hover:text-lime-300">{signal}</p>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12 flex justify-end"><Link to="/servicos" className="group inline-flex items-center gap-4 text-sm font-bold">Ver nossas soluções <span className="grid h-10 w-10 place-items-center border border-white/18 transition-colors group-hover:border-lime-300 group-hover:text-lime-300"><ArrowUpRight size={17} /></span></Link></Reveal>
        </div>
      </section>

      <Process />
      <FinalCta />
    </main>
  );
};
